import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "../../hooks/useTranslation";

const FilterSidebar = ({ filters, onFilterChange, onFeatureToggle, onResetFilters }) => {
  const { t } = useTranslation('catalog');
  // Use dates from filters prop instead of local state
  const pickupDate = filters?.pickupDate || "";
  const dropoffDate = filters?.dropoffDate || "";
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectionMode, setSelectionMode] = useState('pickup'); // 'pickup' or 'dropoff'
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const pickupRef = useRef(null);
  const dropoffRef = useRef(null);
  const calendarRef = useRef(null);

  // Filter section toggles
  const [showTravelDate, setShowTravelDate] = useState(true);
  const [showFeatures, setShowFeatures] = useState(true);
  const [showTravelers, setShowTravelers] = useState(true);
  const [showBeds, setShowBeds] = useState(true);

  // Filter selections from props
  const travelers = filters?.travelers || "1-2";
  // beds and features commented out - kept for future use
  // const beds = filters?.beds || "1-2";
  // const features = filters?.features || {};

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showCalendar &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        !pickupRef.current?.contains(event.target) &&
        !dropoffRef.current?.contains(event.target)
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleDateSelect = (date) => {
    // Create date string without timezone offset
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateString = `${year}-${month}-${day}`;


    if (selectionMode === 'pickup') {
      onFilterChange('pickupDate', dateString);
      // If dropoff date exists and is before new pickup date, clear it
      if (dropoffDate && new Date(dropoffDate) <= new Date(dateString)) {
        onFilterChange('dropoffDate', '');
      }
      setSelectionMode('dropoff');
      // Keep calendar open for dropoff selection
    } else {
      // Ensure dropoff is after pickup
      if (pickupDate && new Date(dateString) > new Date(pickupDate)) {
        onFilterChange('dropoffDate', dateString);
        setShowCalendar(false);
        setSelectionMode('pickup'); // Reset for next time
      }
    }
  };

  const navigateMonth = (direction) => {
    if (direction === "prev") {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const generateCalendarDays = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time for accurate comparison

    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

    // Get the first day of the week (Sunday = 0)
    const startDay = firstDayOfMonth.getDay();

    // Parse selected dates for range highlighting
    const pickupDateObj = pickupDate ? new Date(pickupDate) : null;
    const dropoffDateObj = dropoffDate ? new Date(dropoffDate) : null;
    if (pickupDateObj) pickupDateObj.setHours(0, 0, 0, 0);
    if (dropoffDateObj) dropoffDateObj.setHours(0, 0, 0, 0);

    // Helper function to check if date is in range
    const isInRange = (date) => {
      if (!pickupDateObj || !dropoffDateObj) return false;
      return date > pickupDateObj && date < dropoffDateObj;
    };

    const days = [];

    // Add previous month's trailing days
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const prevMonthLastDay = new Date(prevYear, prevMonth + 1, 0).getDate();

    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(prevYear, prevMonth, prevMonthLastDay - i);
      date.setHours(0, 0, 0, 0);

      const isPast = date < today;
      const currentDateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const isSelected = currentDateString === pickupDate || currentDateString === dropoffDate;
      const isInDateRange = isInRange(date);

      days.push({
        date,
        day: date.getDate(),
        isCurrentMonth: false,
        isToday: false,
        isPast,
        isSelected,
        isInRange: isInDateRange,
      });
    }

    // Add current month's days
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const date = new Date(currentYear, currentMonth, day);
      date.setHours(0, 0, 0, 0);

      const isToday = date.getTime() === today.getTime();
      const isPast = date < today;
      const currentDateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const isSelected = currentDateString === pickupDate || currentDateString === dropoffDate;
      const isInDateRange = isInRange(date);

      days.push({
        date,
        day: day,
        isCurrentMonth: true,
        isToday,
        isPast,
        isSelected,
        isInRange: isInDateRange,
      });
    }

    // Add next month's leading days to fill the grid
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;

    let nextMonthDay = 1;
    while (days.length < 42) {
      const date = new Date(nextYear, nextMonth, nextMonthDay);
      date.setHours(0, 0, 0, 0);

      const isPast = date < today;
      const currentDateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const isSelected = currentDateString === pickupDate || currentDateString === dropoffDate;
      const isInDateRange = isInRange(date);

      days.push({
        date,
        day: nextMonthDay,
        isCurrentMonth: false,
        isToday: false,
        isPast,
        isSelected,
        isInRange: isInDateRange,
      });

      nextMonthDay++;
    }

    return days;
  };

  const handleDateClick = (type) => {
    setSelectionMode(type);
    setShowCalendar(true);
  };

  // Section toggle handlers
  const toggleSection = (section) => {
    switch (section) {
      case "travelDate":
        setShowTravelDate(!showTravelDate);
        break;
      case "features":
        setShowFeatures(!showFeatures);
        break;
      case "travelers":
        setShowTravelers(!showTravelers);
        break;
      case "beds":
        setShowBeds(!showBeds);
        break;
      default:
        console.warn(`Unknown section: ${section}`);
        break;
    }
  };

  // Checkbox handlers - commented out for future use
  // const handleFeatureChange = (feature) => {
  //   if (onFeatureToggle) {
  //     onFeatureToggle(feature, !features[feature]);
  //   }
  // };

  // Radio button handlers
  const handleTravelersChange = (value) => {
    if (onFilterChange) {
      onFilterChange("travelers", value);
    }
  };

  // Beds handler - commented out for future use
  // const handleBedsChange = (value) => {
  //   if (onFilterChange) {
  //     onFilterChange("beds", value);
  //   }
  // };

  return (
    <div className="filter-sidebar">
      <div className="filter-container">
        {/* Reset Filters Button */}
        {onResetFilters && (
          <div className="reset-filters-section">
            <button
              className="reset-filters-btn"
              onClick={onResetFilters}
            >
              {t('filters.reset')}
            </button>
          </div>
        )}

        {/* Travel Date Section */}
        <div className="filter-section">
          <div
            className="section-header"
            onClick={() => toggleSection("travelDate")}
            style={{ cursor: "pointer" }}
          >
            <div className="section-title">{t('filters.travelDate.title')}</div>
            <div
              className="dropdown-icon"
              style={{
                transform: showTravelDate ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <svg
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 9.5L12 15.5L6 9.5"
                  stroke="#009B31"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </div>
          </div>

          {showTravelDate && (
            <div className="date-inputs">
              <div className="date-input-wrapper" ref={pickupRef}>
                <div
                  className="date-input"
                  onClick={() => handleDateClick("pickup")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="input-content">
                    <div className="input-text">
                      {pickupDate ? formatDate(pickupDate) : t('filters.travelDate.selectDate')}
                    </div>
                    <div className="input-label">{t('filters.travelDate.pickupLabel')}</div>
                  </div>
                  <div className="input-icon-container">
                    <div className="input-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M21 10H3M16 2V6M8 2V6M7.8 22H16.2C17.8802 22 18.7202 22 19.362 21.673C19.9265 21.3854 20.3854 20.9265 20.673 20.362C21 19.7202 21 18.8802 21 17.2V8.8C21 7.11984 21 6.27976 20.673 5.63803C20.3854 5.07354 19.9265 4.6146 19.362 4.32698C18.7202 4 17.8802 4 16.2 4H7.8C6.11984 4 5.27976 4 4.63803 4.32698C4.07354 4.6146 3.6146 5.07354 3.32698 5.63803C3 6.27976 3 7.11984 3 8.8V17.2C3 18.8802 3 19.7202 3.32698 20.362C3.6146 20.9265 4.07354 21.3854 4.63803 21.673C5.27976 22 6.11984 22 7.8 22Z"
                          stroke="#009B31"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div className="date-input-wrapper" ref={dropoffRef}>
                <div
                  className="date-input"
                  onClick={() => handleDateClick("dropoff")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="input-content">
                    <div className="input-text">
                      {dropoffDate ? formatDate(dropoffDate) : t('filters.travelDate.selectDate')}
                    </div>
                    <div className="input-label">{t('filters.travelDate.dropoffLabel')}</div>
                  </div>
                  <div className="input-icon-container">
                    <div className="input-icon">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M21 10H3M16 2V6M8 2V6M7.8 22H16.2C17.8802 22 18.7202 22 19.362 21.673C19.9265 21.3854 20.3854 20.9265 20.673 20.362C21 19.7202 21 18.8802 21 17.2V8.8C21 7.11984 21 6.27976 20.673 5.63803C20.3854 5.07354 19.9265 4.6146 19.362 4.32698C18.7202 4 17.8802 4 16.2 4H7.8C6.11984 4 5.27976 4 4.63803 4.32698C4.07354 4.6146 3.6146 5.07354 3.32698 5.63803C3 6.27976 3 7.11984 3 8.8V17.2C3 18.8802 3 19.7202 3.32698 20.362C3.6146 20.9265 4.07354 21.3854 4.63803 21.673C5.27976 22 6.11984 22 7.8 22Z"
                          stroke="#009B31"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                    </div>
                  </div>
                </div>
                {/* Shared Calendar - positioned relative to pickup wrapper */}
                {showCalendar && (
                  <div className="calendar-dropdown" ref={calendarRef}>
                    <div style={{ padding: '8px 16px', borderBottom: '1px solid #eee', fontSize: '14px', fontWeight: '500', color: '#009B31' }}>
                      {selectionMode === 'pickup' ? t('filters.travelDate.selectPickup') : t('filters.travelDate.selectDropoff')}
                    </div>
                    <div className="calendar-header">
                      <button
                        className="calendar-nav-btn"
                        onClick={() => navigateMonth("prev")}
                      >
                        &#8249;
                      </button>
                      <h4>
                        {new Date(currentYear, currentMonth).toLocaleDateString(
                          "en-US",
                          { month: "long", year: "numeric" },
                        )}
                      </h4>
                      <button
                        className="calendar-nav-btn"
                        onClick={() => navigateMonth("next")}
                      >
                        &#8250;
                      </button>
                    </div>
                    <div className="calendar-grid">
                      <div className="calendar-weekdays">
                        {[
                          t('calendar.weekdays.sun'),
                          t('calendar.weekdays.mon'),
                          t('calendar.weekdays.tue'),
                          t('calendar.weekdays.wed'),
                          t('calendar.weekdays.thu'),
                          t('calendar.weekdays.fri'),
                          t('calendar.weekdays.sat')
                        ].map((day, index) => (
                            <div key={index} className="weekday">
                              {day}
                            </div>
                          ),
                        )}
                      </div>
                      <div className="calendar-days">
                        {generateCalendarDays().map(
                          (dayInfo, index) => (
                            <button
                              key={index}
                              className={`calendar-day ${dayInfo.isCurrentMonth ? "current-month" : ""} ${dayInfo.isToday ? "today" : ""} ${dayInfo.isPast ? "past" : ""} ${dayInfo.isSelected ? "selected" : ""} ${dayInfo.isInRange ? "in-range" : ""}`}
                              onClick={() =>
                                !dayInfo.isPast &&
                                handleDateSelect(dayInfo.date)
                              }
                              disabled={dayInfo.isPast}
                            >
                              {dayInfo.day}
                            </button>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* <div className="filter-divider"></div> */}

        {/* Camper-Van Features Section - COMMENTED OUT: Features come already included with all vehicles */}
        {/*
        <div className="filter-section">
          <div
            className="section-header"
            onClick={() => toggleSection("features")}
            style={{ cursor: "pointer" }}
          >
            <div className="section-title">Camper-Van Features</div>
            <div
              className="dropdown-icon"
              style={{
                transform: showFeatures ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <svg
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 9.5L12 15.5L6 9.5"
                  stroke="#009B31"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </div>
          </div>

          {showFeatures && (
            <div className="checkbox-group">
              <div
                className="checkbox-item"
                onClick={() => handleFeatureChange("solar")}
                style={{ cursor: "pointer" }}
              >
                <div className="checkbox">
                  <div
                    className="checkbox-container"
                    style={{
                      backgroundColor: features.solar
                        ? "#009B31"
                        : "transparent",
                      border: "1.2px solid #49454f",
                    }}
                  >
                    {features.solar && (
                      <span style={{ color: "white", fontSize: "12px" }}>
                        ✓
                      </span>
                    )}
                  </div>
                </div>
                <div className="checkbox-label">Solar-Powered</div>
              </div>
              <div
                className="checkbox-item"
                onClick={() => handleFeatureChange("kitchen")}
                style={{ cursor: "pointer" }}
              >
                <div className="checkbox">
                  <div
                    className="checkbox-container"
                    style={{
                      backgroundColor: features.kitchen
                        ? "#009B31"
                        : "transparent",
                      border: "1.2px solid #49454f",
                    }}
                  >
                    {features.kitchen && (
                      <span style={{ color: "white", fontSize: "12px" }}>
                        ✓
                      </span>
                    )}
                  </div>
                </div>
                <div className="checkbox-label">Kitchen</div>
              </div>
              <div
                className="checkbox-item"
                onClick={() => handleFeatureChange("wifi")}
                style={{ cursor: "pointer" }}
              >
                <div className="checkbox">
                  <div
                    className="checkbox-container"
                    style={{
                      backgroundColor: features.wifi
                        ? "#009B31"
                        : "transparent",
                      border: "1.2px solid #49454f",
                    }}
                  >
                    {features.wifi && (
                      <span style={{ color: "white", fontSize: "12px" }}>
                        ✓
                      </span>
                    )}
                  </div>
                </div>
                <div className="checkbox-label">WiFi</div>
              </div>
              <div
                className="checkbox-item"
                onClick={() => handleFeatureChange("wheelchair")}
                style={{ cursor: "pointer" }}
              >
                <div className="checkbox">
                  <div
                    className="checkbox-container"
                    style={{
                      backgroundColor: features.wheelchair
                        ? "#009B31"
                        : "transparent",
                      border: "1.2px solid #49454f",
                    }}
                  >
                    {features.wheelchair && (
                      <span style={{ color: "white", fontSize: "12px" }}>
                        ✓
                      </span>
                    )}
                  </div>
                </div>
                <div className="checkbox-label">Wheelchair Accessible</div>
              </div>
              <div
                className="checkbox-item"
                onClick={() => handleFeatureChange("pet")}
                style={{ cursor: "pointer" }}
              >
                <div className="checkbox">
                  <div
                    className="checkbox-container"
                    style={{
                      backgroundColor: features.pet ? "#009B31" : "transparent",
                      border: "1.2px solid #49454f",
                    }}
                  >
                    {features.pet && (
                      <span style={{ color: "white", fontSize: "12px" }}>
                        ✓
                      </span>
                    )}
                  </div>
                </div>
                <div className="checkbox-label">Pet Friendly</div>
              </div>
              <div
                className="checkbox-item"
                onClick={() => handleFeatureChange("shower")}
                style={{ cursor: "pointer" }}
              >
                <div className="checkbox">
                  <div
                    className="checkbox-container"
                    style={{
                      backgroundColor: features.shower
                        ? "#009B31"
                        : "transparent",
                      border: "1.2px solid #49454f",
                    }}
                  >
                    {features.shower && (
                      <span style={{ color: "white", fontSize: "12px" }}>
                        ✓
                      </span>
                    )}
                  </div>
                </div>
                <div className="checkbox-label">Shower &amp; Toilet</div>
              </div>
            </div>
          )}
        </div>
        */}

        <div className="filter-divider"></div>

        {/* Number of Travelers Section */}
        <div className="filter-section">
          <div
            className="section-header"
            onClick={() => toggleSection("travelers")}
            style={{ cursor: "pointer" }}
          >
            <div className="section-title">{t('filters.travelers.title')}</div>
            <div
              className="dropdown-icon"
              style={{
                transform: showTravelers ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <svg
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 9.5L12 15.5L6 9.5"
                  stroke="#009B31"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </div>
          </div>

          {showTravelers && (
            <div className="radio-group">
              <div
                className="radio-item"
                onClick={() => handleTravelersChange("1-2")}
                style={{ cursor: "pointer" }}
              >
                <div className="radio">
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: "2px solid #49454F",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        travelers === "1-2" ? "#009B31" : "transparent",
                    }}
                  >
                    {travelers === "1-2" && (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "white",
                        }}
                      ></div>
                    )}
                  </div>
                </div>
                <div className="radio-label">{t('filters.travelers.1-2')}</div>
              </div>
              <div
                className="radio-item"
                onClick={() => handleTravelersChange("3-4")}
                style={{ cursor: "pointer" }}
              >
                <div className="radio">
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: "2px solid #49454F",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        travelers === "3-4" ? "#009B31" : "transparent",
                    }}
                  >
                    {travelers === "3-4" && (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "white",
                        }}
                      ></div>
                    )}
                  </div>
                </div>
                <div className="radio-label">{t('filters.travelers.3-4')}</div>
              </div>
              <div
                className="radio-item"
                onClick={() => handleTravelersChange("5+")}
                style={{ cursor: "pointer" }}
              >
                <div className="radio">
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: "2px solid #49454F",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        travelers === "5+" ? "#009B31" : "transparent",
                    }}
                  >
                    {travelers === "5+" && (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "white",
                        }}
                      ></div>
                    )}
                  </div>
                </div>
                <div className="radio-label">{t('filters.travelers.5+')}</div>
              </div>
            </div>
          )}
        </div>

        {/* Number of Beds Section - REMOVED: Maximum of 2 beds, filter may be confusing */}
        {/*
        <div className="filter-divider"></div>

        <div className="filter-section">
          <div
            className="section-header"
            onClick={() => toggleSection("beds")}
            style={{ cursor: "pointer" }}
          >
            <div className="section-title">Number of Beds</div>
            <div
              className="dropdown-icon"
              style={{
                transform: showBeds ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <svg
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 9.5L12 15.5L6 9.5"
                  stroke="#009B31"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                ></path>
              </svg>
            </div>
          </div>

          {showBeds && (
            <div className="radio-group">
              <div
                className="radio-item"
                onClick={() => handleBedsChange("1-2")}
                style={{ cursor: "pointer" }}
              >
                <div className="radio">
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: "2px solid #49454F",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        beds === "1-2" ? "#009B31" : "transparent",
                    }}
                  >
                    {beds === "1-2" && (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "white",
                        }}
                      ></div>
                    )}
                  </div>
                </div>
                <div className="radio-label">1–2 Sleeping Areas</div>
              </div>
              <div
                className="radio-item"
                onClick={() => handleBedsChange("3-4")}
                style={{ cursor: "pointer" }}
              >
                <div className="radio">
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: "2px solid #49454F",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor:
                        beds === "3-4" ? "#009B31" : "transparent",
                    }}
                  >
                    {beds === "3-4" && (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: "white",
                        }}
                      ></div>
                    )}
                  </div>
                </div>
                <div className="radio-label">3–4 Sleeping Areas</div>
              </div>
            </div>
          )}
        </div>
        */}
      </div>
    </div>
  );
};

export default FilterSidebar;
